import { paths } from "./userinput/paths";
import { addComponent, removeComponent, defineQuery, hasComponent } from "bitecs";
import {
  Held,
  Holdable,
  Pinned,
  HoveredRemoteRight,
  HeldRemoteRight,
  HoveredRemoteLeft,
  HeldRemoteLeft,
  HoveredHandRight,
  HeldHandRight,
  HoveredHandLeft,
  HeldHandLeft,
  AEntity,
  Locked,
  GrabStartData,
  RemoteRight,
  RemoteLeft,
  HandRight,
  HandLeft
} from "../bit-components";
import { canMove } from "../utils/permissions-utils";
import { anyEntityWith } from "../utils/bit-utils";

const GRAB_REMOTE_RIGHT = paths.actions.cursor.right.grab;
const DROP_REMOTE_RIGHT = paths.actions.cursor.right.drop;
const GRAB_REMOTE_LEFT = paths.actions.cursor.left.grab;
const DROP_REMOTE_LEFT = paths.actions.cursor.left.drop;
const GRAB_HAND_RIGHT = paths.actions.rightHand.grab;
const DROP_HAND_RIGHT = paths.actions.rightHand.drop;
const GRAB_HAND_LEFT = paths.actions.leftHand.grab;
const DROP_HAND_LEFT = paths.actions.leftHand.drop;

function hasPermissionToGrab(world, eid) {
  if (!hasComponent(world, AEntity, eid)) return true;
  return canMove(world.eid2obj.get(eid).el);
}

function grab(world, userinput, queryHovered, held, grabPath) {
  const hovered = queryHovered(world)[0];
  if (
    hovered &&
    !hasComponent(world, Locked, hovered) &&
    !hasComponent(world, Held, hovered) &&
    userinput.get(grabPath) &&
    (!hasComponent(world, Pinned, hovered) || AFRAME.scenes[0].is("frozen")) &&
    hasPermissionToGrab(world, hovered)
  ) {
    addComponent(world, held, hovered);
    addComponent(world, Held, hovered);

    let heldType;
    if (held === HeldRemoteRight) heldType = RemoteRight;
    else if (held === HeldRemoteLeft) heldType = RemoteLeft;
    else if (held === HeldHandRight) heldType = HandRight;
    else if (held === HeldHandLeft) heldType = HandLeft;

    addComponent(world, GrabStartData, hovered);
    const interactorEid = anyEntityWith(world, heldType);
    const objectPos = world.eid2obj.get(hovered).position.clone();
    const interactorPos = world.eid2obj.get(interactorEid).position.clone();

    GrabStartData.objectX[hovered] = objectPos.x;
    GrabStartData.objectY[hovered] = objectPos.y;
    GrabStartData.objectZ[hovered] = objectPos.z;
    GrabStartData.interactorX[hovered] = interactorPos.x;
    GrabStartData.interactorY[hovered] = interactorPos.y;
    GrabStartData.interactorZ[hovered] = interactorPos.z;

    GrabStartData.interactorZ[hovered] = interactorPos.z;
  }
}

function drop(world, userinput, queryHeld, held, dropPath) {
  const heldEid = queryHeld(world)[0];
  if (heldEid && userinput.get(dropPath)) {
    // TODO: Drop on ownership lost
    removeComponent(world, held, heldEid);

    if (
      !hasComponent(world, HeldRemoteRight, heldEid) &&
      !hasComponent(world, HeldRemoteLeft, heldEid) &&
      !hasComponent(world, HeldHandRight, heldEid) &&
      !hasComponent(world, HeldHandLeft, heldEid)
    ) {
      removeComponent(world, Held, heldEid);
    }
  }
}

const queryHeldRemoteRight = defineQuery([Holdable, HeldRemoteRight]);
const queryHoveredRemoteRight = defineQuery([Holdable, HoveredRemoteRight]);

const queryHeldRemoteLeft = defineQuery([Holdable, HeldRemoteLeft]);
const queryHoveredRemoteLeft = defineQuery([Holdable, HoveredRemoteLeft]);

const queryHeldHandRight = defineQuery([Holdable, HeldHandRight]);
const queryHoveredHandRight = defineQuery([Holdable, HoveredHandRight]);

const queryHeldHandLeft = defineQuery([Holdable, HeldHandLeft]);
const queryHoveredHandLeft = defineQuery([Holdable, HoveredHandLeft]);

export function holdSystem(world, userinput) {
  grab(world, userinput, queryHoveredRemoteRight, HeldRemoteRight, GRAB_REMOTE_RIGHT);
  grab(world, userinput, queryHoveredRemoteLeft, HeldRemoteLeft, GRAB_REMOTE_LEFT);
  grab(world, userinput, queryHoveredHandRight, HeldHandRight, GRAB_HAND_RIGHT);
  grab(world, userinput, queryHoveredHandLeft, HeldHandLeft, GRAB_HAND_LEFT);

  drop(world, userinput, queryHeldRemoteRight, HeldRemoteRight, DROP_REMOTE_RIGHT);
  drop(world, userinput, queryHeldRemoteLeft, HeldRemoteLeft, DROP_REMOTE_LEFT);
  drop(world, userinput, queryHeldHandRight, HeldHandRight, DROP_HAND_RIGHT);
  drop(world, userinput, queryHeldHandLeft, HeldHandLeft, DROP_HAND_LEFT);
}

function hasAnyHeldComponent(world, eid) {
  return (
    hasComponent(world, HeldRemoteRight, eid) ||
    hasComponent(world, HeldRemoteLeft, eid) ||
    hasComponent(world, HeldHandRight, eid) ||
    hasComponent(world, HeldHandLeft, eid)
  );
}

export function dropAllHeld(world) {
  // Find all held objects
  const heldRemoteRight = queryHeldRemoteRight(world);
  const heldRemoteLeft = queryHeldRemoteLeft(world);
  const heldHandRight = queryHeldHandRight(world);
  const heldHandLeft = queryHeldHandLeft(world);

  // Drop all held objects
  for (const eid of heldRemoteRight) {
    removeComponent(world, HeldRemoteRight, eid);
    if (!hasAnyHeldComponent(world, eid)) {
      removeComponent(world, Held, eid);
    }
  }

  for (const eid of heldRemoteLeft) {
    removeComponent(world, HeldRemoteLeft, eid);
    if (!hasAnyHeldComponent(world, eid)) {
      removeComponent(world, Held, eid);
    }
  }

  for (const eid of heldHandRight) {
    removeComponent(world, HeldHandRight, eid);
    if (!hasAnyHeldComponent(world, eid)) {
      removeComponent(world, Held, eid);
    }
  }

  for (const eid of heldHandLeft) {
    removeComponent(world, HeldHandLeft, eid);
    if (!hasAnyHeldComponent(world, eid)) {
      removeComponent(world, Held, eid);
    }
  }
}
